import { TextareaHTMLAttributes, forwardRef, useId, useState } from "react";
import { cx } from "../../utils/cx";
import styles from "./Textarea.module.scss";

export interface TextareaProps
    extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
    label: string;
    helperText?: string;
    error?: boolean | string;
    required?: boolean;
    fullWidth?: boolean;
    /** Highlights the field border (e.g. unsaved edits). */
    modified?: boolean;
    fieldClassName?: string;
    textareaClassName?: string;
    /**
     * Soft character counter (does not block typing).
     * - `number`: show `{n}/{limit}` and turn red when over the limit
     * - `true`: use `maxLength` as the limit when provided
     * Hard limit remains native `maxLength`.
     */
    showCount?: boolean | number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    function Textarea(
        {
            label,
            helperText,
            error,
            required,
            fullWidth = false,
            modified = false,
            fieldClassName,
            textareaClassName,
            showCount,
            id,
            className,
            disabled,
            rows = 5,
            maxLength,
            value,
            defaultValue,
            onChange,
            ...rest
        },
        ref,
    ) {
        const reactId = useId();
        const textareaId = id ?? `ds-textarea-${reactId}`;
        const messageId = `${textareaId}-msg`;
        const countId = `${textareaId}-count`;

        const hasError = Boolean(error);
        const message = typeof error === "string" ? error : helperText;

        const isControlled = value !== undefined;
        const [uncontrolledLength, setUncontrolledLength] = useState(
            String(defaultValue ?? "").length,
        );
        const currentLength = isControlled
            ? String(value ?? "").length
            : uncontrolledLength;

        const countLimit =
            typeof showCount === "number"
                ? showCount
                : showCount === true && typeof maxLength === "number"
                ? maxLength
                : null;
        const shouldShowCount =
            showCount === true || typeof showCount === "number";
        const isOverLimit =
            typeof countLimit === "number" && currentLength > countLimit;

        const describedBy = [
            message ? messageId : null,
            shouldShowCount ? countId : null,
        ]
            .filter(Boolean)
            .join(" ");

        return (
            <div
                className={cx(
                    styles.root,
                    fullWidth && styles.fullWidth,
                    className,
                )}
                data-disabled={disabled || undefined}
                data-error={hasError || undefined}
                data-modified={modified || undefined}
                data-ui="textarea"
            >
                <label
                    className={cx(styles.field, fieldClassName)}
                    htmlFor={textareaId}
                >
                    <span className={styles.labelInputWrap}>
                        <textarea
                            aria-describedby={describedBy || undefined}
                            aria-invalid={hasError || undefined}
                            className={cx(styles.textarea, textareaClassName)}
                            defaultValue={defaultValue}
                            disabled={disabled}
                            id={textareaId}
                            maxLength={maxLength}
                            onChange={event => {
                                if (!isControlled) {
                                    setUncontrolledLength(
                                        event.target.value.length,
                                    );
                                }
                                onChange?.(event);
                            }}
                            placeholder=" "
                            ref={ref}
                            required={required}
                            rows={rows}
                            value={value}
                            {...rest}
                        />
                        <span className={styles.label}>
                            {label}
                            {required ? " *" : ""}
                        </span>
                    </span>
                </label>

                {message ? (
                    <div className={styles.helper} id={messageId}>
                        {message}
                    </div>
                ) : null}

                {shouldShowCount ? (
                    <div
                        className={cx(
                            styles.charCount,
                            isOverLimit && styles.charCountError,
                        )}
                        id={countId}
                    >
                        {typeof countLimit === "number"
                            ? `${currentLength}/${countLimit} characters`
                            : `${currentLength} characters`}
                    </div>
                ) : null}
            </div>
        );
    },
);

Textarea.displayName = "Textarea";
